import { useEffect } from 'react'
import { useTier } from './tiers'

/** Interactive chrome that leans toward the pointer (U13). */
const MAGNET_SELECTOR = '.v2-btn, .btn, .v2-iconbtn, .hv2-quick-tile, .mv2-chip, .hermes-tab'
/** Displacement factor + clamp — subtle lean, never a jump. */
const PULL = 0.16
const MAX_PX = 5

/**
 * Magnetic hover (R14 heavy default): ONE delegated pointermove
 * listener nudges the hovered control toward the cursor with a
 * compositor-only transform; effects.css springs it back on leave.
 * Mounted by the shell; inert in Work Mode / under PRM (the tier is
 * already PRM-clamped) and on coarse pointers.
 */
export function MagneticFx() {
  const { isWork } = useTier()

  useEffect(() => {
    if (isWork) return
    if (typeof window === 'undefined') return
    if (!window.matchMedia('(pointer: fine)').matches) return

    let raf = 0
    let current: HTMLElement | null = null

    function release() {
      if (current) {
        current.style.transform = ''
        current.removeAttribute('data-fx-magnet')
        current = null
      }
    }

    function onMove(e: PointerEvent) {
      const hit = (e.target as Element | null)?.closest?.(MAGNET_SELECTOR) as HTMLElement | null
      if (hit !== current) {
        release()
        current = hit
        current?.setAttribute('data-fx-magnet', '')
      }
      if (!current) return
      const r = current.getBoundingClientRect()
      const dx = Math.max(-MAX_PX, Math.min(MAX_PX, (e.clientX - (r.left + r.width / 2)) * PULL))
      const dy = Math.max(-MAX_PX, Math.min(MAX_PX, (e.clientY - (r.top + r.height / 2)) * PULL))
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        if (current) current.style.transform = `translate3d(${dx}px, ${dy}px, 0)`
      })
    }

    function onLeaveDoc() {
      cancelAnimationFrame(raf)
      release()
    }

    document.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerleave', onLeaveDoc)
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeaveDoc)
      cancelAnimationFrame(raf)
      release()
    }
  }, [isWork])

  return null
}
